import { flatMap, isEqual } from 'lodash';
import { AdditionalClassificationDto, BindingGroupDto, InputGroupDto } from '../types';
import { InputMeta } from '../../iota_applications/types';
import { InputFieldSettingDto } from '../../iota_applications/types/ApplicationDefinition';
import { flattenClassifications } from './classifications';
import { hasBindingValue } from './serializeBindingGroupContent';

export type ClassificationConflict =
    | { kind: 'binding'; reference: string; groupId: string; groupName: string; rowId: string }
    | { kind: 'field-setting'; reference: string };

export const formatClassificationConflict = (conflict: ClassificationConflict): string =>
    conflict.kind === 'binding'
        ? `input group "${conflict.groupName}" (${conflict.groupId}), row "${conflict.rowId}", binding "${conflict.reference}"`
        : `shared Field Settings override "${conflict.reference}"`;

export const formatClassificationConflicts = (
    conflicts: ClassificationConflict[],
    action: 'importing' | 'saving'
): string =>
    `Classification replacement would invalidate: ${conflicts
        .map(formatClassificationConflict)
        .join('; ')}. Resolve these references before ${action}.`;

const isClassificationField = (field: InputMeta): boolean =>
    !!field.isAdditionalClassification || field.section === 'Additional Classifications';

const parentIds = (field: InputMeta): string[] =>
    Array.from(new Set((field.parentInputIds ?? [field.parentInputId]).filter((id): id is string => !!id))).sort();

const standardParents = (definitions: AdditionalClassificationDto[], fieldId: string) =>
    Array.from(
        new Set(
            definitions
                .filter(definition => definition.classificationSlot === fieldId)
                .map(definition => definition.standardParent?.typeCode ?? null)
        )
    ).sort();

// Resolve exact IDs and then suffixes longest-first, preserving layout-instance binding semantics.
export function createFieldResolver(inputs: InputMeta[]) {
    const bySection = new Map<string, Map<string, InputMeta>>();
    for (const input of inputs) {
        const section = (input.section ?? '').replace(/\s+/g, '');
        let fields = bySection.get(section);
        if (!fields) {
            fields = new Map();
            bySection.set(section, fields);
        }
        if (!fields.has(input.id)) fields.set(input.id, input);
    }
    return (section: string, inputId: string): InputMeta | undefined => {
        const fields = bySection.get(section);
        if (!fields) return undefined;
        let suffix = inputId;
        while (true) {
            const input = fields.get(suffix);
            if (input) return input;
            const separator = suffix.indexOf('_');
            if (separator < 0) return undefined;
            suffix = suffix.slice(separator + 1);
        }
    };
}

export function findClassificationConflicts(
    beforeDefs: AdditionalClassificationDto[],
    classifications: AdditionalClassificationDto[],
    inputs: InputMeta[],
    next: InputMeta[],
    groups: InputGroupDto[],
    settings: InputFieldSettingDto[]
): ClassificationConflict[] {
    const beforeDefinitions = flattenClassifications(beforeDefs);
    const afterDefinitions = flattenClassifications(classifications);
    const affected = inputs.filter(field => {
        if (!isClassificationField(field)) {
            return false;
        }
        const replacement = next.find(candidate => candidate.id === field.id && candidate.section === field.section);
        const before = beforeDefinitions.find(definition => definition.classificationSlot === field.id);
        const after = afterDefinitions.find(definition => definition.classificationSlot === field.id);
        return (
            !replacement ||
            before?.typeCode !== after?.typeCode ||
            !isEqual(standardParents(beforeDefinitions, field.id), standardParents(afterDefinitions, field.id)) ||
            field.type !== replacement.type ||
            field.description !== replacement.description ||
            !isEqual(parentIds(field), parentIds(replacement))
        );
    });
    let added: boolean;
    do {
        added = false;
        for (const field of inputs) {
            if (!isClassificationField(field) || affected.includes(field)) {
                continue;
            }
            const parents = parentIds(field);
            if (affected.some(parent => parent.section === field.section && parents.includes(parent.id))) {
                affected.push(field);
                added = true;
            }
        }
    } while (added);
    const resolve = createFieldResolver(inputs);
    const affectedFields = new Set(affected);
    const affectedIds = new Set(affected.map(field => field.id));
    const references = new Map<string, ClassificationConflict>();
    const addConflict = (conflict: ClassificationConflict) => references.set(JSON.stringify(conflict), conflict);
    const rows = (groups: BindingGroupDto[]): BindingGroupDto[] =>
        flatMap(groups, row => [row, ...rows(row.childBindingGroups)]);
    for (const group of groups) {
        for (const root of group.bindingGroups) {
            for (const row of rows([root])) {
                for (const binding of row.bindings) {
                    if (!hasBindingValue(binding)) {
                        continue;
                    }
                    const boundField = resolve(root.bindingGroupId, binding.inputId);
                    if (boundField && affectedFields.has(boundField)) {
                        addConflict({
                            kind: 'binding',
                            reference: binding.inputId,
                            groupId: group.inputGroupId,
                            groupName: group.name,
                            rowId: row.bindingGroupId
                        });
                    }
                }
            }
        }
    }
    for (const setting of settings) {
        if ((setting.hidden != null || setting.required != null) && affectedIds.has(setting.inputId)) {
            addConflict({ kind: 'field-setting', reference: setting.inputId });
        }
    }
    return Array.from(references.values());
}
